import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { DashboardLayout } from "@/components/florist-dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ChevronLeft, Truck, Package, Clock, Mail, Phone, RefreshCw, CreditCard, Calendar, MapPin } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { OrderStatus } from "@/types/order";
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { cn } from "@/lib/utils";
import { OrderStatusActions } from '@/components/florist-dashboard/orders/OrderStatusActions';
import { OrderStatusTimeline } from "@/components/shared/OrderStatusTimeline";

// Helper function to calculate distance between two coordinates using the Haversine formula
const calculateDistance = (coord1: { lat: number; lng: number }, coord2: { lat: number; lng: number }): number => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (coord2.lat - coord1.lat) * Math.PI / 180;
  const dLon = (coord2.lng - coord1.lng) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(coord1.lat * Math.PI / 180) * Math.cos(coord2.lat * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c; // Distance in kilometers
};

import type { Database } from "@/types/database.types";

// Construct a runtime-accurate type from the live SQL you provided
type OrderWithStore = {
  id: string;
  customer_id?: string | null;
  florist_id?: string | null;
  status?: string | null;
  total_amount: number | string; // stored as numeric in DB
  commission_amount: number | string;
  delivery_address?: string | null;
  delivery_time?: string | null;
  is_delivery?: boolean | null;
  stripe_payment_intent_id?: string | null;
  created_at: string;
  updated_at: string;
  gift_message?: string | null;
  order_number?: string | null;
  customer_email?: string | null;
  customer_name?: string | null;
  customer_phone?: string | null;
  payment_status?: string | null;
  delivery_type?: string | null;
  delivery_date?: string | null;
  is_same_day?: boolean | null;
  special_instructions?: string | null;
  items?: any; // jsonb array
  buyer_first_name?: string | null;
  buyer_last_name?: string | null;
  buyer_email?: string | null;
  buyer_phone?: string | null;
  recipient_first_name?: string | null;
  recipient_last_name?: string | null;
  recipient_phone?: string | null;
  is_gift?: boolean | null;
  delivery_coordinates?: any;
  delivery_fee?: number | string | null;
  store_profiles?: { id: string; coordinates?: any } | null;
};

const FloristOrderDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isFlorist, loading: authLoading } = useAuth();

  const fetchOrderDetails = async (): Promise<OrderWithStore> => {
    if (!id || !user) throw new Error("User or Order ID not available");

        // Fetch order details, including coordinates
        const { data: orderData, error: orderError } = await supabase
          .from("orders")
          .select(`
            *,
            delivery_coordinates,
            store_profiles!inner( 
              id,
              coordinates
            )
          `)
          .eq("id", id)
          .single();

        if (orderError) {
          console.error("Error fetching order details (incl. coords):", orderError);
          throw orderError;
        }

    // Authorization Check
        const loggedInUserId = user?.id;
        if (!orderData.store_profiles || orderData.store_profiles.id !== orderData.florist_id) {
             const { data: storeOwnerCheck } = await supabase
          .from("store_profiles")
          .select("id")
        .eq("id", orderData.florist_id)
        .eq("florist_id", loggedInUserId)
        .maybeSingle();
             if (!storeOwnerCheck) {
                toast.error("Unauthorized to view this order (Ownership check failed).");
          throw new Error("Unauthorized");
             }
             console.warn("Store profile join might have failed, but ownership confirmed via secondary check.");
        }
    return orderData as unknown as OrderWithStore;
  };

  const { data: order, isLoading, error, refetch } = useQuery<OrderWithStore>({
    queryKey: ['order', id, user?.id],
    queryFn: fetchOrderDetails,
    enabled: !authLoading && !!user && !!isFlorist && !!id,
  });

  // Fallback image map for legacy orders missing image fields
  const [fallbackProductImages, setFallbackProductImages] = useState<Record<string, string>>({});

  // When order loads, attempt to fetch missing product images by product_id or name
  useEffect(() => {
    if (!order) return;

    // Parse items in a tolerant way
    let parsedItems: any[] = [];
    try {
      if (order.items && typeof order.items === 'string') parsedItems = JSON.parse(order.items);
      else if (Array.isArray(order.items)) parsedItems = order.items as any[];
    } catch (_) {
      parsedItems = [];
    }

    const itemsNeedingImages = parsedItems.filter((it) => {
      const hasAnyImage = !!(it?.main_image_url || it?.image || it?.image_url || (Array.isArray(it?.images) && it.images[0]));
      const isSms = typeof it?.name === 'string' && it.name.toLowerCase().includes('sms');
      return !hasAnyImage && !isSms;
    });

    if (itemsNeedingImages.length === 0) return;

    const byProductId = Array.from(new Set(itemsNeedingImages.map((it) => it.product_id).filter(Boolean)));
    const byName = Array.from(new Set(itemsNeedingImages.filter((it) => !it.product_id && it.name).map((it) => String(it.name))));

    (async () => {
      const newMap: Record<string, string> = {};

      // Lookup by product_id first (most reliable)
      if (byProductId.length > 0) {
        const { data } = await supabase
          .from('products')
          .select('id, main_image_url')
          .in('id', byProductId as any);
        (data || []).forEach((p: any) => {
          if (p?.id && p?.main_image_url) newMap[p.id] = p.main_image_url as string;
        });
      }

      // Fallback: lookup by product title within this florist's catalog (query per title to avoid PostgREST in() quoting issues)
      if (byName.length > 0 && order.florist_id) {
        const lookups = byName.map(async (title) => {
          const { data: p } = await supabase
            .from('products')
            .select('id, title, main_image_url')
            .eq('store_id', order.florist_id as any)
            .eq('title', title)
            .maybeSingle();
          if (p?.title && p?.main_image_url) {
            newMap[String(p.title)] = p.main_image_url as string;
          }
        });
        await Promise.all(lookups);
      }

      if (Object.keys(newMap).length > 0) {
        setFallbackProductImages((prev) => ({ ...prev, ...newMap }));
      }
    })();
  }, [order]);

  // Derive earnings in a single place so UI stays consistent
  const earnings = useMemo(() => {
    if (!order) return null as any;

    const totalOrder = Number(order.total_amount) || 0;
    const deliveryFee = Number(order.delivery_fee ?? 0) || 0;
    const smsFee = Number(order.sms_updates_fee ?? 0) || 0;

    // Try to build product subtotal from line items (excluding SMS-like items)
    let parsedItems: any[] = [];
    try {
      if (order.items && typeof order.items === 'string') {
        parsedItems = JSON.parse(order.items);
      } else if (Array.isArray(order.items)) {
        parsedItems = order.items as any[];
      }
    } catch (_) {
      parsedItems = [];
    }

    const itemsSubtotal = parsedItems
      .filter((it) => it && typeof it === 'object' && typeof it.name === 'string' && !it.name.toLowerCase().includes('sms'))
      .reduce((sum, it) => sum + (Number(it.price) || 0) * (Number(it.quantity ?? 1) || 1), 0);

    // Fallback to total - delivery - sms if items are unavailable
    const productTotal = itemsSubtotal > 0 ? itemsSubtotal : Math.max(0, totalOrder - deliveryFee - smsFee);

    // Commission: prefer stored amount, otherwise compute from rate (default 10%)
    const commissionRate = Number((order as any).commission_rate ?? 0.10);
    const commissionAmount = order.commission_amount !== undefined && order.commission_amount !== null
      ? Number(order.commission_amount)
      : Number((productTotal * commissionRate).toFixed(2));

    const netPayout = Number((productTotal - commissionAmount + deliveryFee).toFixed(2));

    return {
      productTotal: Number(productTotal.toFixed(2)),
      deliveryFee: Number(deliveryFee.toFixed(2)),
      commissionAmount: Number(commissionAmount.toFixed(2)),
      commissionPercentLabel: `${Math.round(commissionRate * 100)}%`,
      netPayout,
    };
  }, [order]);

  // Add a useEffect for redirection based on auth state
  useEffect(() => {
    if (authLoading) return; // Wait for auth
    if (!user) {
      navigate("/login");
      return;
    }
    if (!isFlorist) {
      navigate("/"); // Redirect if not a florist
    }
  }, [user, isFlorist, authLoading, navigate]);

  const sendMessageToCustomer = async () => {
    // messaging removed
  };

  const initiateRefund = async () => {
    try {
      console.log('Initiating refund for order:', id);
      
      // Call the Edge Function with the refund-payment method
      const { data, error } = await supabase.functions.invoke('refund-payment', {
        body: { 
          orderId: id,
          reason: 'requested_by_customer'
        }
      });

      console.log('Refund response:', data, error);

      if (error) {
        throw new Error(`Edge Function error: ${error.message}`);
      }

      if (!data || !data.success) {
        const errorMsg = data?.error || 'Unknown error occurred';
        throw new Error(`Refund failed: ${errorMsg}`);
      }
      
      // Update local state to reflect the refund
      refetch();
      
      toast.success("Refund processed successfully");
    } catch (error) {
      console.error("Error processing refund:", error);
      toast.error(error instanceof Error ? error.message : "Failed to process refund");
    }
  };

  if (authLoading || isLoading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <RefreshCw className="h-8 w-8 animate-spin text-brand-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (error || !order) {
    return (
      <DashboardLayout>
        <div className="p-8">
          <Card>
            <CardContent className="p-6">
              <div className="text-center py-8 space-y-4">
                <h2 className="text-xl font-semibold">Order Not Found or Error</h2>
                <p>{error ? error.message : "We couldn't find the order you're looking for."}</p>
                <Button onClick={() => navigate("/florist-dashboard/orders")}>
                  Return to Orders
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-8">
        <div className="max-w-[1200px] mx-auto space-y-6">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate("/florist-dashboard/orders")}
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back to Orders
            </Button>
            
            <div className="flex items-center gap-2">
              {/* Messaging removed; please contact customer directly via these details */}
               
              {/* <Dialog>
                <DialogTrigger asChild>
                  <Button variant="destructive">
                    <CreditCard className="h-4 w-4 mr-2" />
                    Issue Refund
                  </Button>
                </DialogTrigger>
              </Dialog> */}
              {/* Add Note about Square refunds */}
              <p className="text-sm text-muted-foreground">(Refunds must be processed via the Stripe Dashboard)</p>
            </div>
          </div>

          <div className="grid gap-6">
            {/* Order Summary */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Order #{order.id.slice(0, 8)}</CardTitle>
                    <CardDescription>
                      Placed on {new Date(order.created_at).toLocaleDateString()}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Badge variant="outline" className={
                      order.status === "completed" ? "bg-green-100 text-green-800" :
                      order.status === "processing" ? "bg-blue-100 text-blue-800" :
                      order.status === "cancelled" ? "bg-red-100 text-red-800" :
                      order.status === "delivered" ? "bg-green-100 text-green-800" :
                      "bg-yellow-100 text-yellow-800"
                    }>
                      {order.status.replace("_", " ")}
                    </Badge>
                    {order.delivery_type === 'pickup' && (
                      <Badge className="bg-purple-100 text-purple-800 border-purple-200 font-bold text-xs px-3 py-1">
                        🌸 PICKUP
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-x-8 gap-y-6">
                  {/* Column 1: Timeline */}
                  <div className="md:col-span-1 mb-6 md:mb-0">
                    <h3 className="text-xl font-semibold mb-3 text-gray-800">Order Progress</h3>
                    <OrderStatusTimeline currentStatus={order.status} createdAt={order.created_at} />
                  </div>

                  {/* Column 2: Customer Information */}
                  <div className="md:col-span-1">
                    <h3 className="text-xl font-semibold mb-3 text-gray-800">Customer Information</h3>
                    <div className="space-y-2 text-sm">
                      <p className="flex items-center">
                        <span className="font-medium mr-2 text-gray-700">Name:</span>
                        <span className="text-gray-600">{order.customer?.full_name ?? (order.buyer_first_name && order.buyer_last_name ? `${order.buyer_first_name} ${order.buyer_last_name}` : order.customer_name ?? 'Unknown Name')}</span>
                      </p>
                      <p className="flex items-center">
                        <Mail className="h-4 w-4 mr-2 text-gray-500" />
                        <span className="text-gray-600">{order.customer?.email ?? order.buyer_email ?? order.customer_email ?? 'No email provided'}</span>
                      </p>
                      <p className="flex items-center">
                        <Phone className="h-4 w-4 mr-2 text-gray-500" />
                        <span className="text-gray-600">{order.customer?.phone ?? order.buyer_phone ?? order.customer_phone ?? 'No phone provided'}</span>
                      </p>
                    </div>
                  </div>
                  
                  {/* Column 3: Delivery & Recipient */}
                  <div className="md:col-span-1">
                    <h3 className="text-xl font-semibold mb-3 text-gray-800">
                      {order.delivery_type === 'pickup' ? 'Pickup Details' : 'Delivery Details'}
                    </h3>
                    <div className="space-y-2 text-sm">
                      <p className="flex items-center">
                        <Calendar className="h-4 w-4 mr-2 text-gray-500" />
                        <span className="text-gray-600">
                          {order.delivery_date 
                            ? new Date(order.delivery_date).toLocaleDateString('en-AU', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })
                            : 'No delivery date set'
                          }
                        </span>
                      </p>
                      <p className="flex items-center">
                        <MapPin className="h-4 w-4 mr-2 text-gray-500" />
                        <span className="text-gray-600">
                          {order.delivery_type === 'pickup' 
                            ? 'Customer pickup at store location'
                            : (() => {
                                if (order.delivery_address && typeof order.delivery_address === 'string') {
                                  try {
                                    const parsedAddress = JSON.parse(order.delivery_address);
                                    // Check for V1 (Type, Street, Suburb) or V2 (street, suburb) formats
                                    if (parsedAddress.Street && parsedAddress.Suburb && parsedAddress.State && parsedAddress.Postcode) { // V1
                                      return `${parsedAddress.Street}, ${parsedAddress.Suburb}, ${parsedAddress.State} ${parsedAddress.Postcode}`;
                                    } else if (parsedAddress.street && parsedAddress.suburb && parsedAddress.state && parsedAddress.postcode) { // V2
                                      return `${parsedAddress.street}, ${parsedAddress.suburb}, ${parsedAddress.state} ${parsedAddress.postcode}`;
                                    } else {
                                      // If parsing works but fields are unexpected, show the raw string (it might be a simple non-JSON address)
                                      return order.delivery_address;
                                    }
                                  } catch (e) {
                                    // If JSON parsing fails, it might be a simple non-JSON address string
                                    return order.delivery_address;
                                  }
                                } else if (order.delivery_address) {
                                  // If it's not a string but exists (e.g. already an object, though less likely for this field based on usage)
                                  return String(order.delivery_address);
                                }
                                return 'No delivery address provided';
                              })()
                          }
                        </span>
                      </p>

                      {order.special_instructions && (
                        <div className="mt-3 pt-3 border-t">
                          <p><span className="font-semibold text-gray-700">Instructions / Gift Message:</span></p>
                          <p className="text-gray-600 pl-2 whitespace-pre-wrap">{order.special_instructions}</p>
                        </div>
                      )}
                    
                      {(order.recipient_first_name || order.recipient_last_name || order.recipient_phone || order.gift_message) && (
                        <div className="mt-3 pt-3 border-t">
                          <h4 className="font-semibold mb-1 text-gray-700">Recipient Details</h4>
                          {(order.recipient_first_name || order.recipient_last_name) && (
                            <p><span className="font-medium text-gray-700">Name:</span> <span className="text-gray-600">{order.recipient_first_name} {order.recipient_last_name}</span></p>
                          )}
                          {order.recipient_phone && (
                            <p><span className="font-medium text-gray-700">Phone:</span> <span className="text-gray-600">{order.recipient_phone}</span></p>
                          )}
                          {order.is_gift && order.gift_message && (
                            <div className="mt-2">
                              <p><span className="font-medium text-gray-700">Gift Message (Separate):</span></p>
                              <p className="italic text-gray-600 pl-2 whitespace-pre-wrap">{order.gift_message}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <div className="w-full space-y-4">
                  <h3 className="font-medium">Update Order Status</h3>
                  <div className="flex flex-wrap gap-2">
                    {/* Log the order object just before rendering OrderStatusActions */}
                    {console.log("[FloristOrderDetails] Order object being passed to OrderStatusActions:", order)}
                    <OrderStatusActions 
                      orderId={order.id}
                      currentStatus={order.status as OrderStatus}
                      stripePaymentIntentId={order.stripe_payment_intent_id}
                    />
                  </div>
                </div>
              </CardFooter>
            </Card>

            {/* Primary Order Info - Most Important First */}
            <div className="space-y-6">
              {/* Quick Summary Card */}
              <Card className="border-brand-primary/20 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-2">
                        {(() => {
                          let parsedItems: any[] = [];
                          try {
                            if (order.items && typeof order.items === 'string') parsedItems = JSON.parse(order.items);
                            else if (Array.isArray(order.items)) parsedItems = order.items as any[];
                          } catch (_) {}
                          const items = parsedItems.filter((it) => it?.name && !String(it.name).toLowerCase().includes('sms'));
                          if (items.length === 1) {
                            return `${items[0].quantity ?? 1}× ${items[0].name}`;
                          } else if (items.length > 1) {
                            const totalQty = items.reduce((s, it) => s + (Number(it.quantity ?? 1) || 1), 0);
                            return `${totalQty} items (${items.length} different products)`;
                          }
                          return 'Order Details';
                        })()}
                      </h2>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {order.delivery_date ? new Date(order.delivery_date).toLocaleDateString('en-AU', {
                            weekday: 'short',
                            day: 'numeric',
                            month: 'short'
                          }) : 'No date set'}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="h-4 w-4" />
                          {order.delivery_suburb || order.delivery_city || 'Location TBD'}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500 uppercase tracking-wide">Your Earnings</p>
                      <p className="text-3xl font-bold text-green-600">${earnings?.netPayout.toFixed(2) || '0.00'}</p>
                    </div>
                  </div>
                  
                  {/* Delivery Type Callout */}
                  <div className="mt-4">
                    <div className={cn(
                      "rounded-lg px-4 py-3 text-center font-medium",
                      order.delivery_type === 'pickup' 
                        ? "bg-amber-50 text-amber-900 border border-amber-200" 
                        : "bg-blue-50 text-blue-900 border border-blue-200"
                    )}>
                      {order.delivery_type === 'pickup' ? (
                        <span className="flex items-center justify-center gap-2">
                          <Package className="h-5 w-5" />
                          Customer will pick up at your shop
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          <Truck className="h-5 w-5" />
                          You will deliver this order
                        </span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

            </div>

            {/* Two Column Layout for Items and Earnings */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Order Items - Left Column */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="h-5 w-5 text-brand-primary" />
                    Order Items
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {(() => {
                      let parsedItems = [];
                      try {
                        if (order?.items && typeof order.items === 'string') {
                          parsedItems = JSON.parse(order.items);
                        } else if (order?.items && Array.isArray(order.items)) {
                          parsedItems = order.items;
                        }
                      } catch (e) {
                        console.error("Failed to parse order.items:", e);
                        return <p className="text-red-500">Error displaying items.</p>;
                      }

                      if (!Array.isArray(parsedItems) || parsedItems.length === 0) {
                        return <p className="text-gray-500">No items information available.</p>;
                      }

                      return parsedItems
                        .filter(item => item.name && !item.name.toLowerCase().includes('sms'))
                        .map((item, index) => (
                        <div key={index} className="flex items-start gap-4 py-4 border-b border-gray-100 last:border-0">
                          <img
                            src={
                              item.main_image_url ||
                              item.image ||
                              item.image_url ||
                              (Array.isArray(item.images) && item.images[0]) ||
                              (item.product_id && fallbackProductImages[item.product_id]) ||
                              (item.name && fallbackProductImages[String(item.name)]) ||
                              '/images/placeholder-product.png'
                            }
                            alt={item.name || 'Product image'}
                            className="h-24 w-24 rounded-lg object-cover border border-gray-200 shadow-sm"
                            loading="lazy"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <h4 className="font-semibold text-gray-900 text-base">
                                  {item.name || 'Unknown Item'}
                                </h4>
                                {item.size && (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mt-1">
                                    Size: {item.size}
                                  </span>
                                )}
                                <p className="text-sm text-gray-600 mt-1">Quantity: {item.quantity ?? 1}</p>
                              </div>
                              <div className="text-right">
                                <p className="font-semibold text-lg">${typeof item.price === 'number' ? item.price.toFixed(2) : '0.00'}</p>
                                <p className="text-xs text-gray-500">per item</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </CardContent>
              </Card>

              {/* Earnings Breakdown - Right Column */}
              <div className="space-y-6">
                {/* Payment Breakdown */}
                <Card className="border-green-200 shadow-sm">
                  <CardHeader className="bg-green-50 pb-4">
                    <CardTitle className="flex items-center gap-2 text-green-800">
                      <CreditCard className="h-5 w-5" />
                      Your Earnings Breakdown
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      {earnings && (
                        <>
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="text-gray-700">Product Revenue</span>
                              <span className="font-semibold text-lg">${earnings.productTotal.toFixed(2)}</span>
                            </div>
                            {earnings.deliveryFee > 0 && (
                              <div className="flex justify-between items-center">
                                <span className="text-gray-700">Delivery Fee</span>
                                <span className="font-semibold text-lg">+${earnings.deliveryFee.toFixed(2)}</span>
                              </div>
                            )}
                            <div className="flex justify-between items-center text-red-600">
                              <span>MarketBloom Fee ({earnings.commissionPercentLabel})</span>
                              <span className="font-semibold">-${earnings.commissionAmount.toFixed(2)}</span>
                            </div>
                          </div>
                          
                          <div className="border-t-2 border-green-200 pt-4">
                            <div className="flex justify-between items-center">
                              <span className="text-lg font-bold text-gray-900">Total Payout</span>
                              <span className="text-2xl font-bold text-green-600">${earnings.netPayout.toFixed(2)}</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">Will be deposited to your account</p>
                          </div>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Customer Details */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Customer Details</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 text-sm">
                      <div>
                        <p className="text-gray-500">Name</p>
                        <p className="font-medium">{order.customer_name || 'Guest Customer'}</p>
                      </div>
                      {order.customer_email && (
                        <div>
                          <p className="text-gray-500">Email</p>
                          <p className="font-medium">{order.customer_email}</p>
                        </div>
                      )}
                      {order.customer_phone && (
                        <div>
                          <p className="text-gray-500">Phone</p>
                          <p className="font-medium">{order.customer_phone}</p>
                        </div>
                      )}
                      {order.special_instructions && (
                        <div>
                          <p className="text-gray-500">Special Instructions</p>
                          <p className="font-medium">{order.special_instructions}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
            {/* Delivery & Payment Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Truck className="h-5 w-5 text-blue-600" />
                  Delivery & Payment Info
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Delivery Information */}
                  {order.delivery_type !== 'pickup' && (
                    <div>
                      <h3 className="font-medium mb-3 text-gray-800 flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        Delivery Information
                      </h3>
                      <div className="bg-blue-50 p-3 rounded-lg space-y-2">
                        {order.delivery_coordinates && order.store_profiles?.coordinates ? (
                          <p className="text-sm">
                            <span className="font-medium">Distance:</span> {calculateDistance(order.delivery_coordinates, order.store_profiles.coordinates).toFixed(2)} km
                          </p>
                        ) : (
                          <p className="text-sm text-gray-500">Distance calculation unavailable</p>
                        )}
                        <p className="text-sm">
                          <span className="font-medium">Delivery Fee:</span> ${order.delivery_fee?.toFixed(2) || '0.00'}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Fulfillment Callout */}
                  <div>
                    <div className="mt-1">
                      <div className={cn(
                        "rounded-md px-3 py-2 text-sm",
                        order.delivery_type === 'pickup' ? "bg-amber-50 text-amber-900 border border-amber-200" : "bg-blue-50 text-blue-900 border border-blue-200"
                      )}>
                        {order.delivery_type === 'pickup' ? (
                          <span>Customer will pick up at your shop</span>
                        ) : (
                          <span>You will deliver this order</span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Payment Status */}
                  <div>
                    <h3 className="font-medium mb-3 text-gray-800 flex items-center gap-2">
                      <CreditCard className="h-4 w-4" />
                      Payment Status
                    </h3>
                    <div className="bg-green-50 p-3 rounded-lg">
                      <div className="text-sm flex items-center">
                        <span className="font-medium mr-2">Status:</span>
                        <Badge className="bg-green-100 text-green-800">{order.payment_status}</Badge>
                      </div>
                      {order.stripe_payment_intent_id && (
                        <p className="text-xs text-gray-500 mt-1">
                          Payment ID: {order.stripe_payment_intent_id.slice(0, 20)}...
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FloristOrderDetails; 